import React from "react";
import styled from 'styled-components';
import { BsYoutube } from "react-icons/bs";
import { FaInstagramSquare } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa";
import { FaLinkedin } from "react-icons/fa";
import { Link } from "react-router-dom";

const ACCENT = "#5b54e6";

export const BodyFooter = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
`;

export const Footer = styled.footer`
  width: 100%;
  color: #fff;
  background: linear-gradient(180deg, #6963f0 0%, ${ACCENT} 55%, #4a44c9 100%);
`;

export const FooterContainer = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 56px 32px 28px;

  @media (max-width: 600px) {
    padding: 40px 20px 20px;
  }
`;

/* ---- Top: brand + link columns + newsletter ---- */
export const TopFooter = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1.3fr 1.4fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 28px;
    text-align: center;
  }
`;

export const Brand = styled.div`
  .logo {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: 0.5px;
    margin: 0 0 12px;
  }
  p {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.75);
    max-width: 240px;
  }
  @media (max-width: 520px) {
    p { max-width: none; margin: 0 auto; }
  }
`;

export const FooterCol = styled.div`
  h3 {
    margin: 0 0 16px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
  }
`;

export const LinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const LinkItem = styled.li`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;

  a {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    transition: color 0.18s ease;
  }
  a:hover {
    color: #fff;
  }
`;

/* ---- Newsletter ---- */
export const NewsletterSection = styled.div`
  h3 {
    margin: 0 0 16px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
  }
  p {
    margin: 0 0 14px;
    font-size: 13.5px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.75);
  }
`;

export const NewsletterForm = styled.form`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  padding: 5px 5px 5px 6px;
  max-width: 320px;

  &:focus-within {
    border-color: rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.16);
  }

  @media (max-width: 520px) {
    margin: 0 auto;
  }
`;

export const StyledEmailInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #fff;
  font-size: 14px;
  padding: 9px 14px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

export const StyledButtonInput = styled.input`
  border: none;
  cursor: pointer;
  background: #fff;
  color: ${ACCENT};
  font-size: 13.5px;
  font-weight: 700;
  padding: 9px 20px;
  border-radius: 999px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px -6px rgba(0, 0, 0, 0.4);
  }
`;

/* ---- Social row ---- */
export const SocialLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 44px;
  padding-top: 26px;
  border-top: 1px solid rgba(255, 255, 255, 0.18);

  .label {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    margin-right: 4px;
  }
`;

export const SocialIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  transition: background 0.18s ease, transform 0.18s ease;

  a {
    color: #fff;
    display: inline-flex;
    font-size: 18px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.28);
    transform: translateY(-2px);
  }
`;

/* ---- Bottom bar ---- */
export const FooterInfo = styled.div`
  background: rgb(24, 24, 27);
  color: rgba(255, 255, 255, 0.7);
`;

export const FooterInfoInner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 18px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 10px;
    text-align: center;
    padding: 18px 20px;
  }
`;

export const LegalInfo = styled.div`
  display: flex;
  gap: 22px;
`;

export const LegalLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.18s ease;

  &:hover {
    color: #fff;
  }
`;

function Footerf() {
  return (
    <BodyFooter>
      <Footer>
        <FooterContainer>
          <TopFooter>
            <Brand>
              <div className="logo">StudyAK</div>
              <p>AI-assisted course and assessment generation for modern instructors and teams.</p>
            </Brand>

            <FooterCol>
              <h3>Company</h3>
              <LinkList>
                <LinkItem><a href="/">Home</a></LinkItem>
                <LinkItem><a href="/Packages">Packages</a></LinkItem>
                <LinkItem><a href="/about">About</a></LinkItem>
                <LinkItem><a href="/contact">Contact</a></LinkItem>
              </LinkList>
            </FooterCol>

            <FooterCol>
              <h3>Help</h3>
              <LinkList>
                <LinkItem><a href="#">Payment</a></LinkItem>
                <LinkItem><a href="#">Cancellation</a></LinkItem>
                <LinkItem><a href="#">FAQ</a></LinkItem>
              </LinkList>
            </FooterCol>

            <FooterCol>
              <h3>Contact Details</h3>
              <LinkList>
                <LinkItem>Email : info@studyak.com</LinkItem>
                <LinkItem>Address : Studyak Corporation</LinkItem>
                <LinkItem>8 The Green, STE 15848 Dover,</LinkItem>
                <LinkItem>DE 19901, United States</LinkItem>
              </LinkList>
            </FooterCol>
          </TopFooter>

          <div style={{ marginTop: "44px", paddingTop: "26px", borderTop: "1px solid rgba(255,255,255,0.18)" }}>
            <NewsletterSection style={{ maxWidth: "420px", margin: "0 auto", textAlign: "center" }}>
              <h3>Subscribe to our newsletter</h3>
              <p>Get product updates and learning tips straight to your inbox.</p>
              <NewsletterForm style={{ margin: "0 auto" }}>
                <StyledEmailInput
                  type="email"
                  name="newsletter_email"
                  id="newsletter_email"
                  placeholder="Enter your email"
                />
                <StyledButtonInput type="button" value="Submit" />
              </NewsletterForm>
            </NewsletterSection>
          </div>

          <SocialLine>
            <span className="label">Follow us</span>
            <SocialIcon><Link to='https://www.instagram.com/teamstudyak/'><FaInstagramSquare /></Link></SocialIcon>
            <SocialIcon><Link to='https://www.youtube.com/@TeamStudYak'><BsYoutube /></Link></SocialIcon>
            <SocialIcon><Link to='https://www.tiktok.com/@teamstudyak'><FaTiktok /></Link></SocialIcon>
            <SocialIcon><Link to='https://www.linkedin.com/company/96214098/admin/dashboard/'><FaLinkedin /></Link></SocialIcon>
          </SocialLine>
        </FooterContainer>

        <FooterInfo>
          <FooterInfoInner>
            <LegalInfo>
              <LegalLink href="#">Terms &amp; Conditions</LegalLink>
              <LegalLink href="#">Privacy Policy</LegalLink>
            </LegalInfo>
            <div>Copyright &copy;2024 | STUDYAK.</div>
          </FooterInfoInner>
        </FooterInfo>
      </Footer>
    </BodyFooter>
  );
}

export default Footerf;
